import { useEffect, useState } from 'react';
import { CheckCircle2, MessageSquareHeart, Loader2, Clock } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { feedbackApi, type ApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { sanitizeFeedbackText, FEEDBACK_MAX_LENGTH } from '../lib/feedbackText';

// Fallback when a 429 arrives without a usable Retry-After header — matches
// the server's 1-minute feedback rate-limit window.
const RETRY_FALLBACK_SECONDS = 60;

export function FeedbackPage() {
  // When signed in, the token lets the server attach the account to the
  // entry's metadata; guests submit anonymously.
  const { token } = useAuth();
  const [text, setText] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error' | 'rateLimited'>('idle');
  const [message, setMessage] = useState('');
  // Seconds until the server accepts submissions again (rate-limit countdown).
  const [retrySeconds, setRetrySeconds] = useState(0);

  useEffect(() => {
    if (status !== 'rateLimited' || retrySeconds <= 0) return;
    const timer = window.setTimeout(() => setRetrySeconds(s => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [status, retrySeconds]);

  const waiting = status === 'rateLimited' && retrySeconds > 0;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Sanitize before sending so only clean plain text leaves the browser
    // (the backend runs the same pass again).
    const sanitized = sanitizeFeedbackText(text);
    if (!sanitized) {
      setStatus('error');
      setMessage('Please enter your feedback before sending.');
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await feedbackApi.submit(sanitized, token);
      setStatus('done');
      setMessage(res.message);
      setText('');
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      if (apiErr.status === 429) {
        // Rate-limited: keep the text in the box and count down until the
        // server will accept it, then let them resend.
        setStatus('rateLimited');
        setRetrySeconds(apiErr.retryAfterSeconds ?? RETRY_FALLBACK_SECONDS);
        return;
      }
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <div>
      <section className="bg-cream py-12">
        <div className="container-max">
          <h1 className="text-4xl font-serif font-bold text-coastal-600 mb-2">Leave Feedback</h1>
          <p className="text-gray-700">
            Tried our products or visited us at a market? We&apos;d love to hear what you think.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-max max-w-2xl mx-auto">
          {status === 'done' ? (
            <Card className="text-center py-12" >
              <div role="status">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h2 className="text-2xl font-serif font-bold text-coastal-700 mb-2">Feedback sent</h2>
                <p className="text-gray-600 mb-6">{message}</p>
                <Button variant="outline" size="sm" onClick={() => { setStatus('idle'); setMessage(''); }}>
                  Leave more feedback
                </Button>
              </div>
            </Card>
          ) : (
            <Card>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-coastal-50 flex items-center justify-center flex-shrink-0">
                  <MessageSquareHeart className="w-6 h-6 text-coastal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-serif font-bold text-coastal-700">Tell us anything</h2>
                  <p className="text-sm text-gray-500">
                    Product ideas, flavor requests, or how your last bag treated you.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="Share your thoughts…"
                  rows={7}
                  required
                  maxLength={FEEDBACK_MAX_LENGTH}
                  disabled={status === 'submitting'}
                  className="w-full px-4 py-3 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-coastal-500"
                />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-gray-400">
                    {text.length}/{FEEDBACK_MAX_LENGTH} characters
                  </span>
                  <Button type="submit" size="sm" disabled={status === 'submitting' || waiting}
                    className="flex items-center gap-2">
                    {status === 'submitting' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {waiting && <Clock className="w-4 h-4" />}
                    {status === 'submitting' ? 'Sending…' : waiting ? `Resend in ${retrySeconds}s` : 'Send feedback'}
                  </Button>
                </div>
                {status === 'rateLimited' && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" role="alert">
                    {waiting
                      ? `Thanks for the enthusiasm! We can only accept a couple of messages per minute — your text is kept right where it is, and you can resend it in ${retrySeconds}s.`
                      : 'You can resend your feedback now.'}
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-sm text-red-600" role="alert">{message}</p>
                )}
              </form>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
